<template>
  <div class="transaction-view-wrapper">
    <!-- Action Buttons - Hidden on Print -->
    <div class="transaction-actions no-print">
      <b-card class="mb-2">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center flex-wrap">
            <b-button
              variant="outline-secondary"
              class="mr-1 mb-50"
              @click="$router.go(-1)"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('accounting_transactions_back') }}
            </b-button>
            <b-button
              variant="outline-primary"
              class="mr-1 mb-50"
              @click="printTransaction"
            >
              <feather-icon
                icon="PrinterIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('accounting_transactions_print') }}
            </b-button>
            <gl-journal-chip
              v-if="item"
              class="mb-50"
              :journal-entry-id="item.journal_entry_id"
              :status="item.status"
            />
            <late-posting-badge
              v-if="item"
              class="mb-50 ml-50"
              :late="!!(item.is_late_posting || (item.journal_entry && item.journal_entry.is_late_posting))"
              :late-from-date="item.late_from_date || (item.journal_entry && item.journal_entry.late_from_date)"
            />
          </div>
          <div
            v-if="item && statusValue === 'pending'"
            class="d-flex"
          >
            <b-button
              v-if="canApprove"
              variant="success"
              class="mr-1"
              @click="approveTransaction"
            >
              <feather-icon
                icon="CheckIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('accounting_transactions_approve') }}
            </b-button>
            <b-button
              v-if="canReject"
              variant="danger"
              @click="voucherKind === 'account' ? cancelTransaction() : rejectVoucher()"
            >
              <feather-icon
                icon="XIcon"
                size="16"
                class="mr-50"
              />
              {{ voucherKind === 'account' ? $t('accounting_transactions_cancel') : $t('accounting_transactions_reject') }}
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Professional Transaction Document -->
    <div
      id="transaction-receipt"
      class="transaction-document"
    >
      <!-- Loading State -->
      <div
        v-if="loadItem"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </div>

      <!-- Transaction Content -->
      <div
        v-else-if="item"
        class="document-content"
      >
        <!-- Document Header -->
        <div class="document-header">
          <div class="header-top">
            <div class="company-info">
              <h2 class="company-name">
                {{ $t('accounting_transactions_company_name') }}
              </h2>
              <p class="company-subtitle">
                {{ $t('accounting_transactions_company_subtitle') }}
              </p>
            </div>
            <div class="document-type">
              <h1 class="document-title">
                {{ $t('accounting_transactions_document_title') }}
              </h1>
              <p class="document-subtitle">
                {{ $t('accounting_transactions_document_subtitle') }}
              </p>
            </div>
          </div>
          <div class="header-divider" />
        </div>

        <!-- Document Body -->
        <div class="document-body">
          <!-- Transaction Details Section -->
          <div class="details-section">
            <table class="info-table">
              <tbody>
                <tr>
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_document_number') }}:</strong>
                  </td>
                  <td class="value-cell">
                    <strong class="document-number">{{ documentNumber }}</strong>
                  </td>
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_date') }}:</strong>
                  </td>
                  <td class="value-cell">
                    {{ formatDate(item.transaction_date) }}
                  </td>
                </tr>
                <tr>
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_type') }}:</strong>
                  </td>
                  <td class="value-cell">
                    <b-badge
                      :variant="getTransactionTypeVariant((item.transaction_type && item.transaction_type.value) || '')"
                      class="badge-formal"
                    >
                      {{ $i18n.locale === 'ar' ? (item.transaction_type && item.transaction_type.label_ar) || '' : (item.transaction_type && item.transaction_type.label) || '' }}
                    </b-badge>
                  </td>
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_status') }}:</strong>
                  </td>
                  <td class="value-cell">
                    <b-badge
                      :variant="getStatusVariant((item.status && item.status.value) || '')"
                      class="badge-formal"
                    >
                      {{ $i18n.locale === 'ar' ? (item.status && item.status.label_ar) || '' : (item.status && item.status.label) || '' }}
                    </b-badge>
                  </td>
                </tr>
                <tr v-if="sourceLine">
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_source') }}:</strong>
                  </td>
                  <td
                    class="value-cell"
                    colspan="3"
                  >
                    {{ sourceLine }}
                  </td>
                </tr>
                <tr v-if="fundSourceLine">
                  <td class="label-cell">
                    <strong>{{ $t('accounting_fund_source') }}:</strong>
                  </td>
                  <td
                    class="value-cell"
                    colspan="3"
                  >
                    {{ fundSourceLine }}
                  </td>
                </tr>
                <tr v-if="paidFromLine">
                  <td class="label-cell">
                    <strong>{{ $t('accounting_paid_from') }}:</strong>
                  </td>
                  <td
                    class="value-cell"
                    colspan="3"
                  >
                    {{ paidFromLine }}
                  </td>
                </tr>
                <tr v-if="receivedByLine">
                  <td class="label-cell">
                    <strong>{{ $t('accounting_received_by') }}:</strong>
                  </td>
                  <td
                    class="value-cell"
                    colspan="3"
                  >
                    {{ receivedByLine }}
                  </td>
                </tr>
                <tr v-if="projectLabel">
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_project') }}:</strong>
                  </td>
                  <td
                    class="value-cell"
                    colspan="3"
                  >
                    {{ projectLabel }}
                  </td>
                </tr>
                <tr v-if="reasonText">
                  <td class="label-cell">
                    <strong>{{ $t('accounting_transactions_reason') }}:</strong>
                  </td>
                  <td
                    class="value-cell"
                    colspan="3"
                  >
                    {{ reasonText }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Amount Section -->
          <div class="amount-section">
            <div class="amount-box">
              <div class="amount-label">
                {{ $t('accounting_transactions_amount') }}
              </div>
              <div class="amount-value">
                {{ formatCurrency(item.amount, item.currency) }}
              </div>
              <div class="amount-words">
                {{ $t('accounting_transactions_fee') }}: {{ formatCurrency(item.fee || 0, item.currency) }}
              </div>
              <div class="amount-words">
                {{ $t('accounting_transactions_amount_in_words') }}: {{ amountInWords }}
              </div>
              <div
                v-if="invoiceFile"
                class="amount-words"
              >
                <a
                  :href="invoiceFile.url || invoiceFile.download_url"
                  target="_blank"
                >
                  {{ invoiceFile.original_name || invoiceFile.file_name || $t('accounting_transactions_invoice') }}
                </a>
              </div>
            </div>
          </div>

          <!-- Accounts Information -->
          <div class="accounts-section">
            <table class="accounts-table">
              <thead>
                <tr>
                  <th
                    v-if="item.from_account_id"
                    class="account-header"
                  >
                    {{ $t('accounting_transactions_from_account') }}
                  </th>
                  <th
                    v-if="item.to_account_id"
                    class="account-header"
                  >
                    {{ $t('accounting_transactions_to_account') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    v-if="item.from_account_id"
                    class="account-cell"
                  >
                    <div class="account-details">
                      <div class="account-row">
                        <span class="account-label">{{ $t('accounting_accounts_code') }}:</span>
                        <span class="account-value">{{ item.from_account && item.from_account.code }}</span>
                      </div>
                      <div class="account-row">
                        <span class="account-label">{{ $t('accounting_accounts_name') }}:</span>
                        <span class="account-value">{{ item.from_account && item.from_account.name }}</span>
                      </div>
                      <div
                        v-if="item.from_account && item.from_account.type"
                        class="account-row"
                      >
                        <span class="account-label">{{ $t('accounting_accounts_type') }}:</span>
                        <span class="account-value">
                          {{ item.from_account.type === 'bank' ? ($i18n.locale === 'ar' ? 'بنك' : 'Bank') : ($i18n.locale === 'ar' ? 'نقدي' : 'Cash') }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td
                    v-if="item.to_account_id"
                    class="account-cell"
                  >
                    <div class="account-details">
                      <div class="account-row">
                        <span class="account-label">{{ $t('accounting_accounts_code') }}:</span>
                        <span class="account-value">{{ item.to_account && item.to_account.code }}</span>
                      </div>
                      <div class="account-row">
                        <span class="account-label">{{ $t('accounting_accounts_name') }}:</span>
                        <span class="account-value">{{ item.to_account && item.to_account.name }}</span>
                      </div>
                      <div
                        v-if="item.to_account && item.to_account.type"
                        class="account-row"
                      >
                        <span class="account-label">{{ $t('accounting_accounts_type') }}:</span>
                        <span class="account-value">
                          {{ item.to_account.type === 'bank' ? ($i18n.locale === 'ar' ? 'بنك' : 'Bank') : ($i18n.locale === 'ar' ? 'نقدي' : 'Cash') }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Description Section -->
          <div
            v-if="item.description"
            class="description-section"
          >
            <div class="section-title">
              {{ $t('accounting_transactions_description') }}
            </div>
            <div class="description-box">
              {{ item.description }}
            </div>
          </div>

          <!-- Entries Table -->
          <div
            v-if="item.entries && item.entries.length > 0"
            class="entries-section"
          >
            <div class="section-title">
              {{ $t('accounting_transactions_entries') }}
            </div>
            <table class="entries-table">
              <thead>
                <tr>
                  <th class="entry-number">
                    {{ $t('accounting_transactions_entry_number') }}
                  </th>
                  <th class="entry-type">
                    {{ $t('accounting_transactions_type') }}
                  </th>
                  <th class="entry-amount">
                    {{ $t('accounting_transactions_amount') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(entry, index) in item.entries"
                  :key="index"
                >
                  <td class="entry-number">
                    {{ index + 1 }}
                  </td>
                  <td class="entry-type">
                    <span
                      class="entry-type-badge"
                      :class="(entry.entry_type && entry.entry_type.value === 'debit') ? 'debit' : 'credit'"
                    >
                      {{ $i18n.locale === 'ar' ? (entry.entry_type && entry.entry_type.label_ar) || '' : (entry.entry_type && entry.entry_type.label) || '' }}
                    </span>
                  </td>
                  <td class="entry-amount">
                    <strong>{{ formatCurrency(entry.amount, item.currency) }}</strong>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colspan="2"
                    class="total-label"
                  >
                    <strong>{{ $t('accounting_transactions_total') }}:</strong>
                  </td>
                  <td class="total-amount">
                    <strong>{{ formatCurrency(item.amount, item.currency) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Footer Section -->
          <div class="document-footer">
            <div class="footer-divider" />
            <div class="signature-section">
              <div class="signature-box">
                <div class="signature-line" />
                <div class="signature-label">
                  {{ $t('accounting_transactions_prepared_by') }}
                </div>
              </div>
              <div class="signature-box">
                <div class="signature-line" />
                <div class="signature-label">
                  {{ $t('accounting_transactions_approved_by') }}
                </div>
              </div>
            </div>
            <div class="footer-note">
              <p>{{ $t('accounting_transactions_receipt_footer') }}</p>
              <p class="print-date">
                {{ $t('accounting_transactions_print_date') }}: {{ formatCurrentDate() }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Activity Logs Section -->
      <b-row
        v-if="hasPermission('view activity_logs')"
        class="mt-3"
      >
        <b-col cols="12">
          <b-card header="سجل النشاطات">
            <ActivityLogs
              :model-type="'transaction'"
              :model-id="Number(transactionId)"
              :show-filters="true"
              :per-page="15"
            />
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BBadge,
  BSpinner,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import Ripple from 'vue-ripple-directive'
import ActivityLogs from '@/components/ActivityLogs.vue'
import GlJournalChip from '@/views/modules/ledger/components/GlJournalChip.vue'
import LatePostingBadge from '@/views/modules/ledger/components/LatePostingBadge.vue'
import { invoiceMedia } from '@/utils/transactionMoney'
import {
  fetchActivePayouts, fundSourceParts, paidFromText,
  paymentSourceValue, receivedByText, voucherErrorMessage, voucherSourceText,
} from '@/utils/voucherSource'

export default {
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BRow,
    BCol,
    ToastificationContent,
    FeatherIcon,
    ActivityLogs,
    GlJournalChip,
    LatePostingBadge,
  },
  directives: {
    Ripple,
  },
  computed: {
    ...mapGetters({
      accountItem: 'accounting/transactions/item',
      accountLoadItem: 'accounting/transactions/loadItem',
      incomeItem: 'accounting/incomeTransactions/item',
      incomeLoadItem: 'accounting/incomeTransactions/loadItem',
      expenseItem: 'accounting/expenseTransactions/item',
      expenseLoadItem: 'accounting/expenseTransactions/loadItem',
    }),
    voucherKind() {
      const name = this.$route.name || ''
      if (name.indexOf('income-transactions') !== -1) return 'income'
      if (name.indexOf('expense-transactions') !== -1) return 'expense'
      return 'account'
    },
    item() {
      if (this.voucherKind === 'income') return this.incomeItem
      if (this.voucherKind === 'expense') return this.expenseItem
      return this.accountItem
    },
    loadItem() {
      if (this.voucherKind === 'income') return this.incomeLoadItem
      if (this.voucherKind === 'expense') return this.expenseLoadItem
      return this.accountLoadItem
    },
    transactionId() {
      return this.$route.params.id
    },
    statusValue() {
      const status = this.item && this.item.status
      if (!status) return ''
      if (typeof status === 'object') return status.value || ''
      return status
    },
    documentNumber() {
      if (!this.item) return ''
      return this.item.transaction_number || this.item.number || ''
    },
    reasonText() {
      if (!this.item) return ''
      return this.formatReason(this.item.transaction_reason || this.item.reason)
    },
    fundSourceLine() {
      const parts = fundSourceParts(this.item)
      return parts ? this.$t(parts.key, parts.params) : ''
    },
    paidFromLine() {
      return paidFromText(this.item)
    },
    receivedByLine() {
      return receivedByText(this.item)
    },
    canApprove() {
      if (this.voucherKind === 'account') return this.hasPermission('approve account transactions')
      return this.hasPermission('approve transactions')
    },
    canReject() {
      if (this.voucherKind === 'account') return this.hasPermission('cancel account transactions')
      return this.hasPermission('reject transactions')
    },
    invoiceFile() {
      return invoiceMedia(this.item)
    },
    sourceLine() {
      if (!this.item) return ''
      const kind = paymentSourceValue(this.item.payment_source)
      const labels = {
        account: this.$t('accounting_transactions_company_account'),
        wallet: this.$t('accounting_transactions_custody_wallet'),
        employee_payment_account: this.$t('accounting_transactions_employee_payment_account'),
      }
      const detail = voucherSourceText(this.item, this.payoutAccounts)
      const title = labels[kind] || ''
      if (title && detail) return `${title}: ${detail}`
      return detail || title
    },
    projectLabel() {
      const project = this.item && this.item.project
      if (project && typeof project === 'object') {
        const name = project.name || ''
        const code = project.project_number || project.code || ''
        if (code && name) return `${code} — ${name}`
        return name || code
      }
      return this.item && this.item.project_id ? `#${this.item.project_id}` : ''
    },
    amountInWords() {
      if (!this.item || !this.item.amount) return ''
      // Simple number to words conversion - you can enhance this with a library
      const { amount } = this.item
      if (this.$i18n.locale === 'ar') {
        return `${this.numberToArabicWords(amount)} ${this.item.currency || 'جنيه'} فقط لا غير`
      }
      return `${this.numberToEnglishWords(amount)} ${this.item.currency || 'EGP'} Only`
    },
  },
  data() {
    return {
      payoutAccounts: [],
    }
  },
  async mounted() {
    this.loadPayoutAccounts()
    await this.loadTransaction()
  },
  watch: {
    '$route.fullPath': function routeChanged() {
      this.loadTransaction()
    },
  },
  methods: {
    async loadPayoutAccounts() {
      try {
        this.payoutAccounts = await fetchActivePayouts()
      } catch (error) {
        this.payoutAccounts = []
      }
    },
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
    formatCurrentDate() {
      return new Date().toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    numberToArabicWords(num) {
      // Simplified version - you can use a library for full conversion
      const ones = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة']
      const tens = ['', 'عشرة', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون']
      if (num < 10) return ones[Math.floor(num)]
      if (num < 100) {
        const ten = Math.floor(num / 10)
        const one = num % 10
        if (one === 0) return tens[ten]
        return `${ones[one]} و${tens[ten]}`
      }
      return num.toString() // Fallback to number
    },
    numberToEnglishWords(num) {
      // Simplified version - you can use a library for full conversion
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
      const tens = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
      if (num < 10) return ones[Math.floor(num)]
      if (num < 100) {
        const ten = Math.floor(num / 10)
        const one = num % 10
        if (one === 0) return tens[ten]
        return `${ones[one]} ${tens[ten]}`
      }
      return num.toString() // Fallback to number
    },
    getTransactionTypeVariant(type) {
      const variants = {
        cash_in: 'success',
        cash_out: 'danger',
        transfer: 'info',
      }
      return variants[type] || 'secondary'
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        approved: 'success',
        cancelled: 'danger',
        rejected: 'danger',
      }
      return variants[status] || 'secondary'
    },
    formatReason(reason) {
      if (!reason) return ''
      // If reason is an object with label/label_ar, use it
      if (typeof reason === 'object' && reason !== null) {
        return this.$i18n.locale === 'ar' ? (reason.label_ar || reason.label || reason.value) : (reason.label || reason.value)
      }
      // If reason is a string, return it as is (backend will handle enum casting)
      return reason
    },
    printTransaction() {
      window.print()
    },
    storeAction(name) {
      if (this.voucherKind === 'income') return `accounting/incomeTransactions/${name}`
      if (this.voucherKind === 'expense') return `accounting/expenseTransactions/${name}`
      return `accounting/transactions/${name}`
    },
    async approveTransaction() {
      try {
        await this.$store.dispatch(this.storeAction('approve'), this.transactionId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.approved_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await this.loadTransaction()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: voucherErrorMessage(error, this.$t('accounting_transactions_messages.error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async rejectVoucher() {
      try {
        await this.$store.dispatch(this.storeAction('reject'), this.transactionId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.rejected_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await this.loadTransaction()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: voucherErrorMessage(error, this.$t('accounting_transactions_messages.error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async cancelTransaction() {
      try {
        await this.$store.dispatch('accounting/transactions/cancel', { id: this.transactionId })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.cancelled_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await this.loadTransaction()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async loadTransaction() {
      await this.$store.dispatch(this.storeAction('get'), this.transactionId)
    },
  },
}
</script>

<style lang="scss" scoped>
.transaction-view-wrapper {
  .transaction-actions {
    margin-bottom: 1.5rem;
  }

  .transaction-document {
    max-width: 210mm; // A4 width
    margin: 0 auto;
    background: #ffffff;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    border: 1px solid #e0e0e0;

    .document-content {
      padding: 20mm;
      font-family: 'Times New Roman', 'Arial', sans-serif;
      color: #000;
      line-height: 1.6;

      // Document Header
      .document-header {
        margin-bottom: 30px;
        border-bottom: 3px double #000;
        padding-bottom: 20px;

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;

          .company-info {
            flex: 1;
            text-align: left;

            .company-name {
              font-size: 24px;
              font-weight: 700;
              color: #000;
              margin-bottom: 5px;
              letter-spacing: 0.5px;
            }

            .company-subtitle {
              font-size: 12px;
              color: #666;
              margin: 0;
            }
          }

          .document-type {
            flex: 1;
            text-align: center;

            .document-title {
              font-size: 28px;
              font-weight: 700;
              color: #000;
              margin-bottom: 5px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }

            .document-subtitle {
              font-size: 12px;
              color: #666;
              margin: 0;
              font-style: italic;
            }
          }
        }

        .header-divider {
          height: 2px;
          background: #000;
          margin-top: 10px;
        }
      }

      // Details Section
      .details-section {
        margin-bottom: 25px;

        .info-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 0;

          td {
            padding: 10px;
            border: 1px solid #000;
            vertical-align: middle;

            &.label-cell {
              width: 20%;
              background: #f5f5f5;
              font-size: 13px;
              text-align: right;
              padding-right: 15px;
            }

            &.value-cell {
              width: 30%;
              font-size: 13px;
              text-align: left;
              padding-left: 15px;

              .document-number {
                font-size: 16px;
                color: #000;
              }
            }
          }

          .badge-formal {
            font-size: 11px;
            padding: 5px 10px;
            font-weight: 600;
            border: 1px solid #000;
          }
        }
      }

      // Amount Section
      .amount-section {
        margin-bottom: 25px;
        border: 2px solid #000;
        padding: 20px;
        background: #fafafa;

        .amount-box {
          text-align: center;

          .amount-label {
            font-size: 14px;
            font-weight: 600;
            color: #333;
            margin-bottom: 10px;
            text-transform: uppercase;
          }

          .amount-value {
            font-size: 32px;
            font-weight: 700;
            color: #000;
            margin-bottom: 10px;
            letter-spacing: 1px;
          }

          .amount-words {
            font-size: 12px;
            color: #555;
            font-style: italic;
            border-top: 1px solid #ccc;
            padding-top: 10px;
            margin-top: 10px;
          }
        }
      }

      // Accounts Section
      .accounts-section {
        margin-bottom: 25px;

        .accounts-table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid #000;

          thead {
            background: #000;
            color: #fff;

            th {
              padding: 12px;
              text-align: center;
              font-weight: 700;
              font-size: 14px;
              text-transform: uppercase;
              border: 1px solid #000;
            }
          }

          tbody {
            td {
              padding: 15px;
              border: 1px solid #000;
              vertical-align: top;

              .account-details {
                .account-row {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
                  font-size: 13px;

                  &:last-child {
                    margin-bottom: 0;
                  }

                  .account-label {
                    font-weight: 600;
                    color: #333;
                  }

                  .account-value {
                    color: #000;
                    font-weight: 500;
                  }
                }
              }
            }
          }
        }
      }

      // Description Section
      .description-section {
        margin-bottom: 25px;

        .section-title {
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 10px;
          text-transform: uppercase;
          border-bottom: 2px solid #000;
          padding-bottom: 5px;
        }

        .description-box {
          border: 1px solid #000;
          padding: 15px;
          background: #fafafa;
          font-size: 13px;
          line-height: 1.8;
          min-height: 60px;
        }
      }

      // Entries Section
      .entries-section {
        margin-bottom: 30px;

        .section-title {
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 10px;
          text-transform: uppercase;
          border-bottom: 2px solid #000;
          padding-bottom: 5px;
        }

        .entries-table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid #000;

          thead {
            background: #000;
            color: #fff;

            th {
              padding: 12px;
              text-align: center;
              font-weight: 700;
              font-size: 13px;
              text-transform: uppercase;
              border: 1px solid #000;

              &.entry-number {
                width: 15%;
              }

              &.entry-type {
                width: 35%;
              }

              &.entry-amount {
                width: 50%;
              }
            }
          }

          tbody {
            tr {
              &:nth-child(even) {
                background: #f9f9f9;
              }

              td {
                padding: 10px;
                border: 1px solid #000;
                text-align: center;
                font-size: 13px;

                .entry-type-badge {
                  display: inline-block;
                  padding: 5px 12px;
                  border-radius: 3px;
                  font-weight: 600;
                  font-size: 11px;

                  &.debit {
                    background: #fee;
                    color: #c00;
                    border: 1px solid #c00;
                  }

                  &.credit {
                    background: #efe;
                    color: #060;
                    border: 1px solid #060;
                  }
                }
              }
            }
          }

          tfoot {
            background: #f0f0f0;
            border-top: 2px solid #000;

            tr {
              td {
                padding: 12px;
                border: 1px solid #000;
                font-size: 14px;

                &.total-label {
                  text-align: right;
                  font-weight: 700;
                  text-transform: uppercase;
                }

                &.total-amount {
                  text-align: center;
                  font-size: 16px;
                }
              }
            }
          }
        }
      }

      // Footer Section
      .document-footer {
        margin-top: 40px;
        padding-top: 20px;

        .footer-divider {
          border-top: 2px double #000;
          margin-bottom: 30px;
        }

        .signature-section {
          display: flex;
          justify-content: space-around;
          margin-bottom: 30px;

          .signature-box {
            width: 200px;
            text-align: center;

            .signature-line {
              border-bottom: 1px solid #000;
              height: 50px;
              margin-bottom: 5px;
            }

            .signature-label {
              font-size: 11px;
              color: #666;
              font-weight: 600;
            }
          }
        }

        .footer-note {
          text-align: center;
          font-size: 10px;
          color: #666;
          line-height: 1.6;

          p {
            margin-bottom: 5px;
          }

          .print-date {
            margin-top: 10px;
            font-style: italic;
          }
        }
      }
    }
  }
}

// Print Styles
@media print {
  body {
    background-color: #fff !important;
  }

  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  footer.footer,
  .customizer-toggle,
  .transaction-actions,
  .no-print {
    display: none !important;
  }

  .content.app-content {
    margin-left: 0 !important;
    padding: 0 !important;
  }

  .transaction-view-wrapper {
    .transaction-document {
      box-shadow: none;
      border: none;
      max-width: 100%;
      margin: 0;
      page-break-after: avoid;

      .document-content {
        padding: 15mm;
        font-size: 12pt;

        .document-header {
          page-break-after: avoid;
        }

        .amount-section {
          page-break-inside: avoid;
        }

        .accounts-section,
        .entries-section {
          page-break-inside: avoid;
        }

        .document-footer {
          page-break-inside: avoid;
          margin-top: 20px;
        }
      }
    }
  }

  @page {
    margin: 10mm;
    size: A4;
  }
}
</style>
